import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalState } from "store/configure-store";
import { selectPatient } from "store/reducers/patient.reducer";
import { connect } from "react-redux";
import {
  loadActiveMedication,
  selectActiveMedication,
  toggleSelectedMedication,
} from "store/actions/medication.actions";
import { selectUser } from "store/reducers/user-context.reducer";
import { PatientType } from "model/patient.model";
import { UserType } from "model/user.model";
import { TFunction } from "i18next";
import MedicationRow from "./MedicationRow";
import { TemporalityEnum } from "common/enums/TemporalityEnum";
import { MedicationEntryType } from "model/medication-entry.model";
import {
  selectMedicationEntries,
  selectSelectedMedicationEntries,
} from "store/reducers/medication.reducer";
import MedicationFlow from "../MedicationFlow/MedicationFlow";
import _ from "lodash";
import FloatingButton from "components/Button/FloatingButton";
import { RouteComponentProps, withRouter } from "react-router";
import LoadingScreen from "main/LoadingScreen";

import {
  ROUTE_MEDICATION,
  ROUTE_MEDICATION_SEARCH,
  ROUTE_MEDICATION_STATUS,
} from "routes.global";
import ListGroup from "@cc/react-components/ListGroup";
import Page from "ui/Page";
import { Card } from "@cc/react-components";
import { PV_MEDICATION_OVERVIEW } from "common/matomo/track-page-view.constants";
import { useTrackPageView } from "common/matomo/track-page-view.hook";

export interface Props {
  patient: PatientType;
  user: UserType;
  medicationEntries: Array<MedicationEntryType>;
  inView: boolean;
}

const MedicationOverviewContainer = ({
  patient,
  user,
  medicationEntries,
  loadActiveMedication,
  selectActiveMedication,
  selectedMedicationEntries,
  toggleSelectedMedication,
  inView,
  history,
}: Props & PropsFromDispatch & PropsFromState & RouteComponentProps) => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(true);
  const [medicationFlowOpen, setMedicationFlowOpen] = useState(false);

  useEffect(() => {
    if (inView && patient.id > -1) {
      loadActiveMedication(user.id, patient.id).then(() => setLoading(false));
    }
  }, [inView, user.id, patient.id, loadActiveMedication]);

  useTrackPageView(PV_MEDICATION_OVERVIEW);

  const onClickMedicationRow = (medicationEntry: MedicationEntryType) => {
    toggleSelectedMedication(
      patient.id,
      user.id,
      medicationEntry
    ).then(() => {});
  };

  const onClickEditMedicationEntry = (medicationEntry: MedicationEntryType) => {
    selectActiveMedication(medicationEntry).then(() => {
      history.replace(ROUTE_MEDICATION_STATUS);
      setMedicationFlowOpen(true);
    });
  };

  const renderResults = (
    medicationEntries: Array<MedicationEntryType>,
    t: TFunction
  ) => {
    if (medicationEntries && medicationEntries.length === 0) {
      return (
        <ListGroup>
          <ListGroup.Item>
            {t("medicationOverview.noMedication")}
          </ListGroup.Item>
        </ListGroup>
      );
    } else {
      const items = medicationEntries.map(
        (medicationEntry: MedicationEntryType) => (
          <MedicationRow
            medicationEntry={medicationEntry}
            key={medicationEntry.id}
            selected={
              _.indexOf(selectedMedicationEntries, medicationEntry.id) >= 0
            }
            onClick={() => onClickMedicationRow(medicationEntry)}
            onEditMedicationEntry={onClickEditMedicationEntry}
          />
        )
      );

      return <ListGroup className="mb-3">{items}</ListGroup>;
    }
  };

  const medicationScreen = () => {
    // Remove negative margins from components as they (for some reason?) cause problems with the swipe right
    return (
      <>
        <h1>{t("medicationOverview.title")}</h1>
        <Card>
          <Card.Header className="m-0 pl-0">
            <Card.Title>{t("medicationOverview.chronic.title")}</Card.Title>
          </Card.Header>
          {renderResults(
            medicationEntries.filter(
              (medicationEntry) =>
                TemporalityEnum.CHRONIC === medicationEntry.temporality
            ),
            t
          )}
        </Card>
        <Card>
          <Card.Header className="m-0 pl-0">
            <Card.Title>{t("medicationOverview.nonChronic.title")}</Card.Title>
          </Card.Header>
          {renderResults(
            medicationEntries.filter(
              (medicationEntry) =>
                TemporalityEnum.CHRONIC !== medicationEntry.temporality
            ),
            t
          )}
        </Card>
      </>
    );
  };

  return (
    <Page.Scrollable>
      <Page.Body className="pb-5">
        <MedicationFlow
          baseLocation={ROUTE_MEDICATION}
          isModalOpen={medicationFlowOpen}
          setModalOpen={setMedicationFlowOpen}
        />
        {loading && <LoadingScreen />}
        {!loading && medicationScreen()}
        {!loading && inView && (
          <FloatingButton
            icon="add"
            placement="bottom"
            side="right"
            action={() => {
              history.replace(ROUTE_MEDICATION_SEARCH);
              setMedicationFlowOpen(true);
            }}
          />
        )}
      </Page.Body>
    </Page.Scrollable>
  );
};

interface PropsFromDispatch {
  loadActiveMedication(userId: number, patientId: number): Promise<void>;
  selectActiveMedication(medicationEntry: MedicationEntryType): Promise<void>;
  toggleSelectedMedication(
    patientId: number,
    userId: number,
    medicationEntry: MedicationEntryType
  ): Promise<void>;
}

interface PropsFromState {
  activeMedicationEntry?: MedicationEntryType;
  patient: PatientType;
  user: UserType;
  medicationEntries: Array<MedicationEntryType>;
  selectedMedicationEntries: Array<number>;
}

const mapStateToProps = (state: GlobalState) => {
  return {
    patient: selectPatient(state),
    user: selectUser(state),
    medicationEntries: selectMedicationEntries(state.medicationContext),
    selectedMedicationEntries: selectSelectedMedicationEntries(
      state.medicationContext
    ),
  };
};

export default connect(mapStateToProps, {
  loadActiveMedication,
  selectActiveMedication,
  toggleSelectedMedication,
})(withRouter(MedicationOverviewContainer));
